import { useState, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, BookOpen, Plus, Save, Trash2, History } from "lucide-react";
import { toast } from "sonner";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  listKnowledge,
  upsertKnowledge,
  deleteKnowledge,
  getKnowledgeHistory,
  type KnowledgeEntry,
} from "@/lib/knowledge.functions";
import { EmentasPanel } from "@/components/admin/EmentasPanel";

export const Route = createFileRoute("/admin/contexto")({
  head: () => ({
    meta: [
      { title: "Contexto da empresa — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/contexto">
      <ContextoPage />
    </AdminGate>
  ),
});

const CATEGORIES = [
  { value: "identidade", label: "Identidade & Marca" },
  { value: "persona", label: "Público-alvo" },
  { value: "produto", label: "Produtos" },
  { value: "precos", label: "Preços" },
  { value: "operacao", label: "Operação" },
  { value: "paginas", label: "Páginas & Links" },
  { value: "lead_source", label: "Fontes de Lead" },
] as const;

const CATEGORY_VALUES = CATEGORIES.map((c) => c.value) as readonly string[];

type FormState = {
  id?: string;
  category: KnowledgeEntry["category"];
  key: string;
  title: string;
  content: string;
  tags: string;
  is_active: boolean;
};

const EMPTY_FORM: FormState = {
  category: "identidade",
  key: "",
  title: "",
  content: "",
  tags: "",
  is_active: true,
};

function ContextoPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listKnowledge);
  const upsertFn = useServerFn(upsertKnowledge);
  const deleteFn = useServerFn(deleteKnowledge);
  const historyFn = useServerFn(getKnowledgeHistory);

  const { data: allEntries = [], isLoading } = useQuery({
    queryKey: ["company_knowledge"],
    queryFn: () => listFn(),
  });

  // Oculta categorias legadas (fision_role, historico) que não fazem mais sentido
  const entries = useMemo(
    () => allEntries.filter((e) => CATEGORY_VALUES.includes(e.category)),
    [allEntries],
  );

  const [filter, setFilter] = useState<string>("all");
  const [editing, setEditing] = useState<FormState | null>(null);
  const [historyFor, setHistoryFor] = useState<KnowledgeEntry | null>(null);

  const grouped = useMemo(() => {
    const filtered = filter === "all" ? entries : entries.filter((e) => e.category === filter);
    const g: Record<string, KnowledgeEntry[]> = {};
    for (const e of filtered) {
      g[e.category] = g[e.category] || [];
      g[e.category].push(e);
    }
    return g;
  }, [entries, filter]);

  const upsertMut = useMutation({
    mutationFn: async (f: FormState) =>
      upsertFn({
        data: {
          id: f.id,
          category: f.category,
          key: f.key.trim(),
          title: f.title.trim(),
          content: f.content,
          tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
          is_active: f.is_active,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company_knowledge"] });
      toast.success("Contexto salvo");
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company_knowledge"] });
      toast.success("Removido");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: history = [] } = useQuery({
    queryKey: ["knowledge_history", historyFor?.id],
    queryFn: () => historyFn({ data: { knowledge_id: historyFor!.id } }),
    enabled: !!historyFor,
  });

  return (
    <div className="min-h-screen text-white">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-white/60 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <BookOpen className="h-7 w-7 text-admin-accent" />
            <div>
              <h1 className="text-2xl font-bold">Contexto da empresa</h1>
              <p className="text-xs text-white/50 uppercase tracking-widest">
                Memória viva · identidade, produtos, preços e operação
              </p>
            </div>
          </div>
          <Button
            onClick={() => setEditing({ ...EMPTY_FORM })}
            className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
          >
            <Plus className="h-4 w-4 mr-2" /> Novo contexto
          </Button>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
            className={filter === "all" ? "bg-[#f5b022] text-[#13123a] hover:bg-[#e09f17]" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            Todos ({entries.length})
          </Button>
          {CATEGORIES.map((c) => {
            const count = entries.filter((e) => e.category === c.value).length;
            return (
              <Button
                key={c.value}
                variant={filter === c.value ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(c.value)}
                className={filter === c.value ? "bg-[#f5b022] text-[#13123a] hover:bg-[#e09f17]" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
              >
                {c.label} ({count})
              </Button>
            );
          })}
        </div>

        {isLoading && <p className="text-white/60">Carregando...</p>}

        <div className="mb-10">
          <EmentasPanel />
        </div>

        <div className="border-t border-white/10 pt-8 mb-4">
          <h2 className="text-base font-semibold text-white">Conhecimento textual</h2>
          <p className="text-xs text-white/50">Identidade, produtos, preços, operação, professores, links — a fonte de verdade da FEO.</p>
        </div>

        <div className="space-y-8">
          {CATEGORIES.map((cat) => {
            const list = grouped[cat.value];
            if (!list || list.length === 0) return null;
            return (
              <section key={cat.value}>
                <h2 className="text-sm uppercase tracking-widest text-admin-accent mb-3">{cat.label}</h2>
                <div className="grid gap-3 md:grid-cols-2">
                  {list.map((entry) => (
                    <div
                      key={entry.id}
                      className={`rounded-xl border p-4 transition ${entry.is_active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-60"}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{entry.title}</h3>
                          <p className="text-xs text-white/40 font-mono">{entry.key} · v{entry.version}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white/60 hover:text-white"
                            onClick={() => setHistoryFor(entry)}
                            title="Histórico de versões"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white/60 hover:text-white"
                            onClick={() =>
                              setEditing({
                                id: entry.id,
                                category: entry.category,
                                key: entry.key,
                                title: entry.title,
                                content: entry.content,
                                tags: entry.tags.join(", "),
                                is_active: entry.is_active,
                              })
                            }
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400/70 hover:text-red-300"
                            onClick={() => {
                              if (confirm(`Remover "${entry.title}"?`)) deleteMut.mutate(entry.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-white/70 line-clamp-3 whitespace-pre-wrap">{entry.content}</p>
                      {entry.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {entry.tags.map((t) => (
                            <Badge key={t} variant="secondary" className="bg-white/10 text-white/70 text-xs">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-[#13123a] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar contexto" : "Novo contexto"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Categoria</Label>
                  <Select
                    value={editing.category}
                    onValueChange={(v) => setEditing({ ...editing, category: v as KnowledgeEntry["category"] })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/80">Chave (única na categoria)</Label>
                  <Input
                    value={editing.key}
                    onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                    placeholder="ex: pos-orto"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80">Título</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white/80">Conteúdo (markdown)</Label>
                <Textarea
                  value={editing.content}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  rows={12}
                  className="bg-white/5 border-white/10 text-white font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-white/80">Tags (separadas por vírgula)</Label>
                <Input
                  value={editing.tags}
                  onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={editing.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                />
                <Label className="text-white/80">Ativo</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  Cancelar
                </Button>
                <Button
                  onClick={() => upsertMut.mutate(editing)}
                  disabled={upsertMut.isPending || !editing.key || !editing.title}
                  className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold"
                >
                  {upsertMut.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-3xl bg-[#13123a] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Histórico — {historyFor?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {history.length === 0 && <p className="text-white/60 text-sm">Sem versões anteriores ainda.</p>}
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between text-xs text-white/50 mb-2">
                  <span>v{h.version}</span>
                  <span>{(() => { try { return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(h.changed_at)); } catch { return h.changed_at; } })()}</span>
                </div>
                <p className="text-sm font-semibold text-white mb-1">{h.title}</p>
                <p className="text-xs text-white/70 whitespace-pre-wrap line-clamp-6">{h.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
